import type { RegisterOptions } from 'react-hook-form'
import { NO_RESERVED_EMAIL_MESSAGE, REQUIRED_MESSAGE } from '@Const/error-messages'
import { Maybe } from '@Types'
import { isNumeric } from '@App/helpers/isNumeric'

export const emailIsValid = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )

export const valueIsEmpty = (value: string) => value.trim().length === 0

export const IPIValidator = (ipi: Maybe<string>, required: boolean | undefined) => {
    if (!ipi && !required) {
        return true
    } else {
        if (ipi) {
            ipi = ipi.trim()
            if (ipi.match(/^[0-9]*$/) && ipi.length >= 9 && ipi.length <= 11) {
                ipi = ipi.padStart(11, '0')

                const check = Number(ipi.substring(9, 11))

                return check === getChecksumFromIpiString(ipi)
            }
        }
        return false
    }
}

export const IsniValidator = (isni: Maybe<string>, required: boolean | undefined) => {
    if (!isni && !required) {
        return true
    } else {
        if (isni) {
            if (!/^[0-9]{15}[0-9X]$/.test(isni)) {
                return false
            }
            const baseDigits = isni.slice(0, 15)
            let total = 0
            for (const digit of baseDigits) {
                total = (total + parseInt(digit, 10)) * 2
            }
            const remainder = total % 11
            const checkDigit = (12 - remainder) % 11
            const expectedCheckCharacter = checkDigit === 10 ? 'X' : checkDigit.toString()
            return isni[15] === expectedCheckCharacter
        }
        return false
    }
}

export const ISWCValidator = (iswc: string) => {
    if (!iswc) {
        return true
    }

    iswc = iswc.replace(/-/g, '').replace(/\./g, '')

    if (iswc.length !== 11 || !iswc.startsWith('T')) {
        return false
    }

    const digits = iswc.substring(1, 11)
    let sum = 1

    for (let i = 0; i <= 8; i += 1) {
        const digit = '' + digits.charAt(i)
        sum = sum + (i + 1) * +digit
    }

    let rem = sum % 10
    if (rem !== 0) {
        rem = 10 - rem
    }

    const check = Number('' + digits.charAt(9))
    return check === rem
}

// This function is similar to IPIValidator above, but is used when forms need to return an error message to the user.
export const checkIPIFormatWithErrors = (value: string) =>
    !value.length
        ? 'Required'
        : value.length > 11
          ? 'Сan not be longer 11 characters'
          : !value.match(/^[0-9]*$/)
            ? 'IPI number must contain only numbers'
            : IPIValidator(value, false)
              ? ''
              : 'Check sum error'

const getChecksumFromIpiString = (ipi: string) => {
    let sum = 0
    let weight = 10
    const digits = ipi.substring(0, 9)

    for (let i = 0; i < 9; i++) {
        const digit = '' + digits.charAt(i)
        sum += weight * +digit
        weight--
    }

    let checkSum = sum % 101

    if (checkSum === 1) {
        checkSum = 0
    }
    if (checkSum !== 0) {
        checkSum = 101 - checkSum
    }
    return checkSum
}

export const isNotAnReservedEmail = (email: string): string | undefined => {
    if (!email.includes('@')) {
        return undefined
    }

    const reservedDomains = ['harryfox.com', 'rumblefish.com', 'sesac.com', 'themlc.com', 'themlc.us']
    const domain = email.split('@').pop()
    const isReserved = reservedDomains.find((reservedDomain: string) => reservedDomain === domain)
    if (isReserved) {
        return NO_RESERVED_EMAIL_MESSAGE
    } else {
        return undefined
    }
}

export const isNotAnReservedEmailBoolean = (email: string): boolean => {
    if (!email.includes('@')) {
        return true
    }

    const reservedDomains = ['harryfox.com', 'rumblefish.com', 'sesac.com', 'themlc.com', 'themlc.us']
    const domain = email.split('@').pop()
    return !reservedDomains.find((reservedDomain: string) => reservedDomain === domain)
}

export const validateUsPostalCode = (postalCode: string) => {
    if (postalCode.includes('-')) {
        const postalCodes = postalCode.split('-')
        if (postalCodes.length > 2) {
            return false
        }
        if (postalCodes[0].length !== 5) {
            return false
        }
        if (!isNumeric(postalCodes[0])) {
            return false
        }
        if (postalCodes[1].length !== 4) {
            return false
        }
        return isNumeric(postalCodes[1])
    } else {
        return postalCode.length === 5 && isNumeric(postalCode)
    }
}

export const setRequiredIfTrue = (isTrue: boolean, registerOptions: RegisterOptions): RegisterOptions =>
    isTrue
        ? {
              ...registerOptions,
              required: REQUIRED_MESSAGE,
          }
        : {
              ...registerOptions,
              required: false,
          }
